import { useMemo, useState } from "react";
import {
  BadgeCheck,
  ShieldAlert,
  ChevronRight,
  ChevronDown,
  Download,
  FileDown,
  XCircle,
  AlertTriangle,
  Info,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadValidationPdf } from "@/lib/api";
import { toast } from "sonner";

const SEV = {
  error: { color: "#F87171", icon: XCircle },
  warning: { color: "#FBBF24", icon: AlertTriangle },
  info: { color: "#71717A", icon: Info },
};
const ST = {
  VALID: { color: "#34D399", icon: CheckCircle2, label: "VALID" },
  WARNING: { color: "#FBBF24", icon: AlertTriangle, label: "WARNING" },
  ERROR: { color: "#F87171", icon: XCircle, label: "ERROR" },
};

function toCSV(v) {
  const rows = [["asset_id", "class", "feature", "status", "severity", "field", "rule", "message"]];
  v.result.assets.forEach((a) => {
    if (!a.issues.length) {
      rows.push([a.asset_id, a.class, a.tag, a.status, "", "", "", "OK"]);
    } else {
      a.issues.forEach((i) => rows.push([a.asset_id, a.class, a.tag, a.status, i.severity, i.field, i.rule, i.message]));
    }
  });
  return rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
}

export default function ValidationResult({ validation }) {
  const { result, file_name } = validation;
  const { summary, assets } = result;
  const [open, setOpen] = useState(() => new Set());
  const compliant = summary.verdict === "COMPLIANT";

  const grouped = useMemo(() => {
    const g = {};
    assets.forEach((a) => (g[a.class] = g[a.class] || []).push(a));
    return Object.keys(g).sort().map((k) => ({ cls: k, items: g[k] }));
  }, [assets]);

  const toggle = (id) =>
    setOpen((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const exportCSV = () => {
    const blob = new Blob([toCSV(validation)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Validation_${(file_name || "report").replace(/[^a-z0-9]+/gi, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPDF = async () => {
    setPdfBusy(true);
    try {
      await downloadValidationPdf(validation.file_id, `ADAC_Validation_${(file_name || "report").replace(/[^a-z0-9]+/gi, "_")}.pdf`);
      toast.success("PDF report downloaded");
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="validation-result">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="font-mono text-xs text-zinc-500">
          <span className="px-2 py-0.5 rounded-sm bg-zinc-900 border border-zinc-800 text-zinc-300">FILE: {file_name}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={exportPDF} data-testid="validation-export-pdf" disabled={pdfBusy} className="rounded-sm bg-blue-600 hover:bg-blue-500 text-white">
            <FileDown className="w-4 h-4 mr-2" /> {pdfBusy ? "Generating…" : "Export PDF"}
          </Button>
          <Button onClick={exportCSV} data-testid="validation-export-csv" variant="outline" className="rounded-sm border-zinc-700 bg-transparent hover:bg-zinc-900 text-zinc-300">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div
          className="border rounded-sm p-5 flex items-center gap-4"
          style={{ borderColor: compliant ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)", background: compliant ? "rgba(16,185,129,0.06)" : "rgba(239,68,68,0.06)" }}
          data-testid="validation-verdict"
        >
          {compliant ? <BadgeCheck className="w-10 h-10 text-emerald-400" /> : <ShieldAlert className="w-10 h-10 text-red-400" />}
          <div>
            <div className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">ADAC Schema</div>
            <div className="font-display text-2xl font-bold" style={{ color: compliant ? "#34D399" : "#F87171" }}>
              {summary.verdict}
            </div>
          </div>
        </div>
        <div className="md:col-span-2 border border-zinc-800 rounded-sm bg-[#101010] p-5">
          <div className="flex items-end justify-between mb-2">
            <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500">Compliance Rate</span>
            <span className="font-display text-3xl font-bold text-zinc-100">{summary.compliance_pct}%</span>
          </div>
          <div className="h-2.5 w-full rounded-sm bg-zinc-800 overflow-hidden">
            <div className="h-full rounded-sm" style={{ width: `${summary.compliance_pct}%`, background: compliant ? "#34D399" : "#FBBF24" }} />
          </div>
          <div className="font-mono text-[11px] text-zinc-500 mt-2">
            {summary.valid}/{summary.total_assets} features valid · {summary.total_errors} errors · {summary.total_warnings} warnings
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Metric label="Valid" value={summary.valid} color="#34D399" testid="val-metric-valid" />
        <Metric label="With Errors" value={summary.with_errors} color="#F87171" testid="val-metric-errors" />
        <Metric label="With Warnings" value={summary.with_warnings} color="#FBBF24" testid="val-metric-warnings" />
        <Metric label="Total Issues" value={summary.total_errors + summary.total_warnings} color="#60A5FA" testid="val-metric-issues" />
      </div>

      {(summary.schema_label || (summary.file_notes || []).length > 0) && (
        <div className="border border-zinc-800 rounded-sm bg-[#101010] p-3 space-y-2" data-testid="validation-schema-banner">
          <div className="flex flex-wrap items-center gap-2 font-mono text-[11px]">
            <span className="px-2 py-0.5 rounded-sm bg-blue-600/15 border border-blue-600/30 text-blue-400">Schema {summary.schema_label || summary.schema_version}</span>
            {summary.expected_datum && <span className="text-zinc-500">expects {summary.expected_datum}</span>}
            {summary.declared_datum && <span className="text-zinc-500">· file declares {summary.declared_datum}</span>}
          </div>
          {(summary.file_notes || []).map((n, i) => (
            <div key={i} className={`font-mono text-[10px] ${n.severity === "warning" ? "text-amber-400" : "text-zinc-500"}`} data-testid={`file-note-${i}`}>• {n.message}</div>
          ))}
        </div>
      )}

      <div className="border border-zinc-800 rounded-sm overflow-hidden">
        <div className="bg-[#101010] px-4 py-2.5 border-b border-zinc-800 font-mono text-[11px] uppercase tracking-wider text-zinc-500">
          Compliance by ADAC service class
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-zinc-500 font-mono text-[11px] uppercase border-b border-zinc-800">
              <th className="text-left font-medium px-4 py-2">Class</th>
              <th className="text-right font-medium px-4 py-2">Total</th>
              <th className="text-right font-medium px-4 py-2 text-emerald-400">Valid</th>
              <th className="text-right font-medium px-4 py-2 text-amber-400">Warning</th>
              <th className="text-right font-medium px-4 py-2 text-red-400">Error</th>
            </tr>
          </thead>
          <tbody>
            {summary.by_class.map((c) => (
              <tr key={c.class} className="border-b border-zinc-900 font-mono text-xs">
                <td className="px-4 py-2.5 text-zinc-200">{c.class}</td>
                <td className="px-4 py-2.5 text-right text-zinc-400">{c.total}</td>
                <td className="px-4 py-2.5 text-right text-emerald-400">{c.valid}</td>
                <td className="px-4 py-2.5 text-right text-amber-400">{c.warning}</td>
                <td className="px-4 py-2.5 text-right text-red-400">{c.error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-5">
        {grouped.map((group) => (
          <div key={group.cls}>
            <h3 className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> {group.cls} ({group.items.length})
            </h3>
            <div className="space-y-2">
              {group.items.map((a) => {
                const st = ST[a.status];
                const StIcon = st.icon;
                const expandable = a.issues.length > 0;
                const isOpen = open.has(a.asset_id + a.tag);
                return (
                  <div key={a.asset_id + a.tag} className="border border-zinc-800 rounded-sm bg-[#101010] overflow-hidden" data-testid={`val-asset-${a.asset_id}`}>
                    <div
                      className={`flex items-center justify-between px-4 py-3 ${expandable ? "cursor-pointer hover:bg-zinc-900/50" : ""}`}
                      onClick={() => expandable && toggle(a.asset_id + a.tag)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-4 text-zinc-600">
                          {expandable ? (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
                        </span>
                        <span className="font-mono text-xs text-zinc-200">{a.asset_id || "(no id)"}</span>
                        <span className="font-mono text-xs text-zinc-500">{a.tag}</span>
                        {!a.known && (
                          <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-zinc-800 border border-zinc-700 text-zinc-400">UNKNOWN FEATURE</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {a.errors > 0 && <span className="font-mono text-[10px] text-red-400">{a.errors} err</span>}
                        {a.warnings > 0 && <span className="font-mono text-[10px] text-amber-400">{a.warnings} warn</span>}
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm font-mono text-[10px] font-semibold" style={{ color: st.color, background: `${st.color}1a`, border: `1px solid ${st.color}40` }}>
                          <StIcon className="w-3 h-3" /> {st.label}
                        </span>
                      </div>
                    </div>
                    {isOpen && expandable && (
                      <div className="border-t border-zinc-800 divide-y divide-zinc-900">
                        {a.issues.map((iss, i) => {
                          const sev = SEV[iss.severity];
                          const SevIcon = sev.icon;
                          return (
                            <div key={i} className="flex items-start gap-3 px-4 py-2.5">
                              <SevIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: sev.color }} />
                              <div className="font-mono text-xs">
                                <span style={{ color: sev.color }}>{iss.message}</span>
                                <span className="text-zinc-600"> · rule {iss.rule}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value, color, testid }) {
  return (
    <div className="border border-zinc-800 rounded-sm bg-[#101010] p-4" data-testid={testid}>
      <div className="font-mono text-[11px] uppercase tracking-wider mb-2" style={{ color }}>{label}</div>
      <div className="font-display text-2xl font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
